import React, { useState, useEffect } from 'react';
import {
  TextInput,
  SimpleSelect,
  InlineTextInput
} from '@youngcapital/component-library';
import { isEmpty } from 'lodash';
import {
  validZipcode,
  validHousenumber,
  validTelephone
} from '@youngcapital/validations';
import * as yup from 'yup';

import { composeAddress } from './helper';
import { asFieldset, FIELDSET_PROPTYPES } from '../asFieldset';
import FormText from '../../FormText';
import { omitFieldsetKeysForDOM } from '../helpers';
import {
  getDefaultSelectedCountryId,
  COUNTRY_ID
} from '../../../helpers/country';
import { whenFieldHasValueHideText } from '../helpers/utils';

const ContactInfoFieldset = ({
  getFieldProps,
  optionsData,
  t,
  fetchAddress
}) => {
  const [notFoundAddress, setNotFoundAddress] = useState(false);

  const countries = optionsData.countries || [];
  const countryField = getFieldProps('home_address.country_id');
  const addressZipcodeField = getFieldProps('home_address.zipcode');
  const {
    validationMessage: houseNumberValidationMessage,
    validationState: houseNumberValidationState,
    ...addressNumberField
  } = getFieldProps('home_address.number');
  const {
    validationMessage: houseUnitValidationMessage,
    validationState: houseUnitValidationState,
    ...addressUnitField
  } = getFieldProps('home_address.unit');

  const cityField = getFieldProps('home_address.city');
  const streetField = getFieldProps('home_address.street');

  const isNetherlands = Number(countryField.value) === COUNTRY_ID.nl;

  const isNetherlandsButNotFoundAddress = isNetherlands && notFoundAddress;

  function setStreetAndCity({ street, city }) {
    const { setValue: setStreet } = streetField;
    const { setValue: setCity } = cityField;

    setStreet(street);
    setCity(city);
  }

  function addressNotFound() {
    setNotFoundAddress(true);
    setStreetAndCity({ street: '', city: '' });
  }

  useEffect(() => {
    const houseNumberValue = addressNumberField.value;
    const zipcodeValue = addressZipcodeField.value;

    if (isNetherlands && zipcodeValue && houseNumberValue) {
      fetchAddress({ number: houseNumberValue, zipcode: zipcodeValue })
        .then(res => {
          setStreetAndCity(res);
          setNotFoundAddress(false);
        })
        .catch(addressNotFound);
    } else if (isEmpty(zipcodeValue) || isEmpty(houseNumberValue)) {
      setStreetAndCity({ street: '', city: '' });
    }
  }, [addressNumberField.value, addressZipcodeField.value, isNetherlands]);

  return (
    <>
      <h3>{t('fieldset_contact_info.header')}</h3>
      <SimpleSelect
        {...getFieldProps('home_address.country_id')}
        label={t('fieldset_contact_info.country_label')}
        isRequired
      >
        {countries.map(option => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </SimpleSelect>

      <TextInput
        {...addressZipcodeField}
        type='text'
        isRequired
        placeholder={t('fieldset_contact_info.zipcode_placeholder')}
        tooltipContent={whenFieldHasValueHideText(
          addressZipcodeField.value,
          t('fieldset_contact_info.zipcode_tooltip')
        )}
        label={t('fieldset_contact_info.zipcode_label')}
        hoverTooltipProps={{ noHover: true }}
      />

      <InlineTextInput
        validationMessage={
          houseNumberValidationMessage || houseUnitValidationMessage
        }
        validationState={
          houseNumberValidationState === 'success'
            ? houseUnitValidationState
            : houseNumberValidationState
        }
        label={t('fieldset_contact_info.number_label')}
        name='number_label'
        isRequired
        hoverTooltipProps={{ noHover: true }}
        inputs={[
          {
            ...omitFieldsetKeysForDOM(addressNumberField),
            placeholder: '',
            type: 'text',
            id: '1'
          },
          {
            ...omitFieldsetKeysForDOM(addressUnitField),
            placeholder: t('fieldset_contact_info.unit_label'),
            type: 'text',
            id: '2'
          }
        ]}
        tooltipPosition='top'
        tooltipContent={whenFieldHasValueHideText(
          addressNumberField.value,
          t('fieldset_contact_info.number_tooltip')
        )}
      />
      {isNetherlandsButNotFoundAddress && (
        <FormText
          paragraphProps={{
            className: 'text--bold'
          }}
          data-testid='address-not-found'
        >
          {t('fieldset_contact_info.address_not_found')}
        </FormText>
      )}
      {!isNetherlands || isNetherlandsButNotFoundAddress ? (
        <>
          <TextInput
            {...streetField}
            type='text'
            isRequired
            label={t('fieldset_contact_info.street_label')}
          />
          <TextInput
            {...cityField}
            type='text'
            isRequired
            label={t('fieldset_contact_info.city_label')}
          />
        </>
      ) : (
        <FormText className='bg--grey pl-2x' data-testid='full-address'>
          {composeAddress(streetField.value, cityField.value)}
        </FormText>
      )}
      <TextInput
        {...getFieldProps('telephone')}
        type='tel'
        isRequired
        placeholder={t('fieldset_contact_info.telephone_placeholder')}
        label={t('fieldset_contact_info.telephone_label')}
        tooltipContent={whenFieldHasValueHideText(
          getFieldProps('telephone').value,
          t('fieldset_contact_info.telephone_tooltip')
        )}
        hoverTooltipProps={{ noHover: true }}
      />
    </>
  );
};

ContactInfoFieldset.type = 'contact-info';

ContactInfoFieldset.propTypes = FIELDSET_PROPTYPES;

ContactInfoFieldset.initialValues = () => ({
  home_address: {
    country_id: getDefaultSelectedCountryId(),
    zipcode: '',
    number: '',
    city: '',
    street: '',
    unit: ''
  },
  telephone: ''
});

ContactInfoFieldset.validationSchema = () => ({
  home_address: yup.object().shape({
    country_id: yup.mixed().required(),
    zipcode: validZipcode().required(),
    number: validHousenumber().required(),
    city: yup.string().required(),
    street: yup.string().required(),
    unit: validHousenumber()
  }),
  telephone: validTelephone().required()
});

export default asFieldset(ContactInfoFieldset, { optionsData: true });
